import { useMemo } from "react";
import { Plus, Save, Trash2, Lock, LockOpen, Sparkles, AlertTriangle, CalendarDays, LayoutGrid, Users, Utensils, Info, Archive } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import BulkEntry from "@/components/poshan/BulkEntry";
import StickyActionBar from "@/components/mobile/StickyActionBar";
import { MONTH_LIST, TARGET_COST } from "@/lib/mealPlan";
import { money, ingredientAmount, toNumber } from "@/lib/poshanConstants";
import { NO_MDM_REASONS, isNoMdmDay, isHolidayDay, isMealDay, reasonLabel } from "@/lib/dayKind";
import { scrollReasonBlockIntoView } from "@/lib/dailyPayload";
import { toast } from "sonner";

// Mobile-only Daily Entry layout (visible on screens < lg). Mirrors the
// supplied mockup: hero title, Single/Bulk pill, selected-date + day-chip
// strip, attendance card (2-col), ingredients card (empty-state with
// Auto-fill + Add Individual Item), per-student target pill, and sticky
// bottom Save button that sits above the BottomNav.
const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function MobileNumField({ label, value, onChange, readOnly, testId }) {
  return (
    <label className="block">
      <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">{label}</span>
      <input
        data-testid={testId}
        type="number"
        inputMode="numeric"
        value={value ?? 0}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1.5 block w-full rounded-xl border border-border px-3 py-2.5 text-center font-mono text-base font-semibold text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30 ${
          readOnly ? "bg-sand-50 text-ink-700" : "bg-white"
        }`}
      />
    </label>
  );
}

function formatLastUpdated(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch (_e) {
    return null;
  }
}

export default function MobileDailyEntry(props) {
  const {
    active, year, month, date,
    setYear, setMonth, setDate,
    draft, mode, setMode,
    monthDays, entries, breakdown, dayTotalsAmount,
    updateAttendance, addIngredient, removeIngredient, updateIngredient,
    autoFillFromMealPlan, handleSave, saving,
    blocked, inputsBlocked, isHoliday, blockedEdit, blockedNew,
    isHardLocked, isSoftLocked, setLockLevel,
    refresh, setDraft,
    ayMonthYearChoices, monthYearValue, onMonthYearChange,
    lockForMonth, schoolId, // eslint-disable-line no-unused-vars
  } = props;

  const monthLabel = MONTH_LIST[month - 1]?.label || "";
  const mealHidden = draft.dayKind === "holiday" || draft.dayKind === "working_no_mdm";
  const lastUpdated = useMemo(() => formatLastUpdated(draft?.updatedAt), [draft?.updatedAt]);

  return (
    <div className="md:hidden" data-testid="mobile-daily-entry">
      {/* Hero */}
      <header className="px-5 pt-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-ink-700">
          Day-by-day kitchen log
        </p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-ink-900">Daily Entry</h1>
        <p className="mt-2 text-sm text-ink-700">
          <b className="text-ink-900">{active?.schoolName || "—"}</b>
          <span className="mx-1.5 text-ink-500">•</span>
          UDISE: {active?.udiseCode || "—"}
        </p>
      </header>

      {/* Mode toggle pill */}
      <section className="mx-4 mt-5 rounded-2xl border border-border bg-white p-1.5 shadow-[0_1px_0_rgba(44,53,40,0.04)]">
        <div className="grid grid-cols-2 gap-1">
          {[
            { v: "single", label: "Single Day", Icon: CalendarDays },
            { v: "bulk",   label: "Bulk Entry", Icon: LayoutGrid },
          ].map((opt) => {
            const activePill = mode === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => setMode(opt.v)}
                data-testid={`m-entry-mode-${opt.v}`}
                className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold transition-all ${
                  activePill
                    ? "bg-clay text-white shadow-[0_6px_14px_-6px_rgb(var(--clay)/0.55)]"
                    : "text-ink-700 active:bg-sand-50"
                }`}
              >
                <opt.Icon className="h-4 w-4" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Date controls */}
      <section className="mx-4 mt-4">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-ink-700">
              Selected Date
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Select value={monthYearValue} onValueChange={onMonthYearChange}>
                <SelectTrigger
                  data-testid="m-month-year-trigger"
                  className="h-10 w-auto gap-1.5 border-0 bg-transparent p-0 font-heading text-xl font-bold text-clay shadow-none focus:ring-0 [&>svg:last-child]:ml-1"
                >
                  <SelectValue>{monthLabel} {year}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {(ayMonthYearChoices || []).map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`m-month-year-${opt.value}`}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Lock toggles */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLockLevel(isSoftLocked ? null : "soft")}
              data-testid="m-soft-lock-btn"
              aria-label={isSoftLocked ? "Soft locked — tap to unlock" : "Soft lock month"}
              className={`grid h-11 w-11 place-items-center rounded-xl border transition-all ${
                isSoftLocked
                  ? "border-ochre bg-ochre/15 text-ochre"
                  : "border-border bg-white text-ink-700 active:bg-sand-50"
              }`}
            >
              {isSoftLocked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => setLockLevel(isHardLocked ? null : "hard")}
              data-testid="m-hard-lock-btn"
              aria-label={isHardLocked ? "Hard locked — tap to unlock" : "Hard lock month"}
              className={`grid h-11 w-11 place-items-center rounded-xl border transition-all ${
                isHardLocked
                  ? "border-ink-900 bg-ink-900 text-white"
                  : "border-border bg-white text-ink-900 active:bg-sand-50"
              }`}
            >
              <Lock className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Day-chip strip — horizontal scrollable */}
        {mode === "single" && (
          <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-2" data-testid="m-date-strip">
            {monthDays.map((d) => {
              const dow = new Date(`${d}T00:00:00`).getDay();
              const entryForDay = entries.find((e) => e.date === d);
              const hasEntry = !!entryForDay;
              const isOffEntry = hasEntry && !isMealDay(entryForDay);
              const dayNum = Number(d.slice(8, 10));
              const isActive = d === date;
              return (
                <button
                  key={d}
                  onClick={() => setDate(d)}
                  data-testid={`m-date-${d}`}
                  data-entry-type={isOffEntry ? (isHolidayDay(entryForDay) ? "HOLIDAY" : "NO_MDM") : hasEntry ? (entryForDay.entryType === "dryration" ? "DR" : "MDM") : undefined}
                  className={`relative grid h-16 w-14 shrink-0 place-items-center rounded-2xl border-2 transition-all ${
                    isActive
                      ? "border-clay bg-clay text-white shadow-[0_8px_18px_-8px_rgb(var(--clay)/0.6)]"
                      : isOffEntry
                      ? "border-slate-300 bg-slate-100 text-slate-600"
                      : hasEntry
                      ? "border-moss/40 bg-white text-ink-900"
                      : dow === 0
                      ? "border-border bg-sand-100 text-ink-500"
                      : "border-border bg-white text-ink-900"
                  }`}
                >
                  <div className="text-center">
                    <span className={`block text-[0.6rem] font-bold uppercase tracking-wider ${isActive ? "opacity-90" : "text-ink-700"}`}>
                      {DOW_LABELS[dow]}
                    </span>
                    <span className="mt-0.5 block text-lg font-bold leading-none">{dayNum}</span>
                  </div>
                  {hasEntry && !isActive && (
                    <span className={`absolute bottom-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full ${isOffEntry ? "bg-slate-400" : "bg-moss-dark"}`} />
                  )}
                  {isActive && hasEntry && (
                    <span className="absolute bottom-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-white" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Lock warnings */}
      {blockedEdit && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-xl border border-clay bg-clay-50 px-3 py-2 text-xs text-clay-dark" data-testid="m-hard-lock-banner">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Month is <b>hard-locked</b> — entries are read-only.</span>
        </div>
      )}
      {blockedNew && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-xl border border-ochre bg-ochre/10 px-3 py-2 text-xs text-ochre" data-testid="m-soft-lock-banner">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Month is <b>soft-locked</b> — new dates can't be added.</span>
        </div>
      )}

      {/* Bulk mode swap-in */}
      {mode === "bulk" ? (
        <section className="mx-4 mt-4 pb-32">
          <BulkEntry
            schoolId={active?.id}
            active={active}
            year={year}
            month={month}
            existingEntries={entries}
            blockedNew={isSoftLocked || isHardLocked}
            blockedEdit={blockedEdit}
            onSaved={() => refresh && refresh()}
          />
        </section>
      ) : (
        <>
          {/* Working Day / Holiday selector — shown above the Day Type toggle
              on mobile too. Defaults Working Day. Holiday disables every
              input below; Save button still works. */}
          <section
            className="mx-4 mt-4 rounded-2xl border border-clay/30 bg-clay/5 p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]"
            data-testid="m-working-holiday-toggle"
            data-active-daykind={draft.dayKind}
          >
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-clay-dark">
              📅 ଦିନ · Day
            </p>
            <p className="mt-1 font-heading text-base font-bold text-ink-900" data-testid="m-daily-date-pretty">
              {(() => {
                const wk = ["ରବିବାର", "ସୋମବାର", "ମଙ୍ଗଳବାର", "ବୁଧବାର", "ଗୁରୁବାର", "ଶୁକ୍ରବାର", "ଶନିବାର"];
                if (!date || date.length < 10) return date || "";
                const [y, m, d] = date.split("-");
                const dt = new Date(`${date}T00:00:00`);
                return `${wk[dt.getDay()] || ""} · ${d}/${m}/${y}`;
              })()}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-1.5 rounded-xl bg-white p-1">
              {[
                { v: "working", label: "MDM · ଭୋଜନ", activeBg: "bg-moss text-white" },
                { v: "working_no_mdm", label: "No MDM · ପରିବେଷଣ ହୋଇନାହିଁ", activeBg: "bg-ochre text-white" },
                { v: "holiday", label: "Holiday · ଛୁଟି", activeBg: "bg-clay text-white" },
              ].map((opt) => {
                const activePill = draft.dayKind === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    disabled={blocked}
                    data-testid={`m-daykind-${opt.v}`}
                    onClick={() => {
                      if (blocked) return;
                      const next = opt.v;
                      setDraft((d) => {
                        // Every day-kind switch starts clean (new + existing entries alike).
                        const nd = {
                          ...d, dayKind: next, entryType: "mdm", ingredients: [],
                          attendance: { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 },
                          hadDryRation: next !== "working" && (d.entryType === "dryration" || !!d.hadDryRation),
                        };
                        if (next !== "working_no_mdm") {
                          nd.noMdmReason = undefined;
                          nd.noMdmReasonOther = undefined;
                          nd.orderNo = undefined;
                          nd.orderDate = undefined;
                        }
                        return nd;
                      });
                      if (next === "working_no_mdm") scrollReasonBlockIntoView();
                      toast.success(
                        next === "holiday" ? "Marked as Holiday · ଛୁଟି"
                        : next === "working_no_mdm" ? "Working Day — No MDM served"
                        : "Marked as Working Day"
                      );
                    }}
                    className={`rounded-lg py-2 text-[0.65rem] font-bold transition-all disabled:opacity-50 ${
                      activePill
                        ? `${opt.activeBg} shadow-[0_4px_10px_-4px_rgb(var(--clay)/0.5)]`
                        : "text-ink-700 active:bg-sand-100"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {draft.dayKind === "working_no_mdm" && (
              <div className="mt-3 rounded-xl border-2 border-ochre/40 bg-ochre/10 p-3" data-testid="m-no-mdm-reason-block">
                <p className="mb-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-ochre-dark">
                  ❗ Reason · କାରଣ <span className="text-rose-600">*</span>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {NO_MDM_REASONS.map((r) => {
                    const active = draft.noMdmReason === r.key;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        data-testid={`m-no-mdm-reason-${r.key}`}
                        onClick={() => setDraft((d) => ({ ...d, noMdmReason: r.key }))}
                        className={`min-h-[44px] rounded-lg border-2 px-2 text-[0.65rem] font-semibold transition-all ${
                          active ? "border-ochre bg-ochre text-white" : "border-slate-200 bg-white text-ink-800"
                        }`}
                      >
                        {r.en}
                      </button>
                    );
                  })}
                </div>
                {draft.noMdmReason === "other" && (
                  <input
                    type="text"
                    data-testid="m-no-mdm-reason-other-input"
                    value={draft.noMdmReasonOther || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, noMdmReasonOther: e.target.value }))}
                    placeholder="Describe reason · କାରଣ"
                    className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  />
                )}
                {draft.noMdmReason === "collector_order" && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input type="text" data-testid="m-no-mdm-order-no" placeholder="Order No."
                      value={draft.orderNo || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, orderNo: e.target.value }))}
                      className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" />
                    <input type="date" data-testid="m-no-mdm-order-date"
                      value={draft.orderDate || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, orderDate: e.target.value }))}
                      className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" />
                  </div>
                )}
                <p className="mt-2 rounded-lg bg-ochre/20 px-2.5 py-1.5 text-[0.65rem] font-semibold text-ochre-dark">
                  ⚠️ Rice & cooking cost will not be deducted. · ଚାଉଳ ଓ ରନ୍ଧନ ଖର୍ଚ୍ଚ କଟା ହେବ ନାହିଁ।
                </p>
                {draft.hadDryRation && (
                  <p className="mt-2 rounded-lg border border-ochre/40 bg-white px-2.5 py-1.5 text-[0.65rem] text-ink-800" data-testid="m-no-mdm-dr-note">
                    Dry ration distributed? Choose Working Day · MDM Served → Dry Ration instead.<br />
                    ଶୁଖିଲା ରାସନ ବଣ୍ଟନ ହୋଇଛି କି? ତେବେ "MDM Served" ବାଛି Dry Ration ବାଛନ୍ତୁ।
                  </p>
                )}
              </div>
            )}
            {isHoliday && (
              <p className="mt-3 rounded-lg border border-clay/40 bg-clay/10 p-2 text-[0.7rem] font-semibold text-clay-dark" data-testid="m-holiday-banner">
                ⛔ ଛୁଟି ଦିନ · Entry fields disabled. Save ଦବାନ୍ତୁ ତାହାହେଲେ ଏହି ଦିନ Holiday ଭାବେ record ହେବ।
              </p>
            )}
          </section>

          {!mealHidden && (<>
          {/* Day Type toggle (MDM / Dry Ration) — hidden on No MDM / Holiday */}
          <section className={`mx-4 mt-4 rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)] ${isHoliday ? "pointer-events-none opacity-50" : ""}`} data-testid="m-entry-type-toggle">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Day Type</span>
              {draft.entryType === "dryration" && (
                <span className="rounded-full bg-ochre/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-ochre" data-testid="m-dry-ration-banner">
                  uses enrolment
                </span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-1.5 rounded-xl bg-sand-50 p-1">
              {[
                { v: "mdm", label: "MDM" },
                { v: "dryration", label: "Dry Ration" },
              ].map((opt) => {
                const activePill = draft.entryType === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    disabled={blocked}
                    data-testid={`m-entry-type-${opt.v}`}
                    onClick={() => {
                      if (blocked) return;
                      const next = opt.v;
                      setDraft((d) => {
                        const newDraft = { ...d, entryType: next };
                        if (next === "dryration" && active) {
                          newDraft.attendance = {
                            balVatika: toNumber(active.balVatikaEnrollment),
                            primary: toNumber(active.primaryEnrollment),
                            upperPrimary: toNumber(active.upperPrimaryEnrollment),
                            highSchool: toNumber(active.highSchoolEnrollment),
                          };
                        }
                        return newDraft;
                      });
                      toast.success(next === "dryration" ? "Dry Ration mode (uses enrolment)" : "MDM mode");
                    }}
                    className={`rounded-lg py-2 text-xs font-bold transition-all disabled:opacity-50 ${
                      activePill
                        ? "bg-clay text-white shadow-[0_4px_10px_-4px_rgb(var(--clay)/0.5)]"
                        : "text-ink-700 active:bg-sand-100"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Today's Attendance */}
          <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-attendance-card">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-ink-900">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-clay/15 text-clay">
                <Users className="h-3.5 w-3.5" />
              </span>
              {draft.entryType === "dryration" ? "Today's Enrolment (Dry Ration)" : "Today's Attendance"}
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <MobileNumField testId="m-att-bv" label="Bal Vatika" value={draft.attendance.balVatika} readOnly={inputsBlocked} onChange={(v) => updateAttendance("balVatika", v)} />
              <MobileNumField testId="m-att-primary" label="Primary" value={draft.attendance.primary} readOnly={inputsBlocked} onChange={(v) => updateAttendance("primary", v)} />
              <MobileNumField testId="m-att-up" label="Upper Primary" value={draft.attendance.upperPrimary} readOnly={inputsBlocked} onChange={(v) => updateAttendance("upperPrimary", v)} />
              <MobileNumField testId="m-att-hs" label="High School" value={draft.attendance.highSchool} readOnly={inputsBlocked} onChange={(v) => updateAttendance("highSchool", v)} />
            </div>
          </section>

          {/* Today's Ingredients */}
          <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-ingredients-card">
            <div className="flex items-center justify-between gap-3">
              <h2 className="flex min-w-0 items-center gap-2 font-heading text-lg font-bold text-ink-900">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-clay/15 text-clay">
                  <Utensils className="h-3.5 w-3.5" />
                </span>
                Today's Ingredients
              </h2>
              <span className="font-heading text-xl font-bold text-clay">{money(dayTotalsAmount)}</span>
            </div>

            {(draft.ingredients || []).length === 0 ? (
              <div className="mt-4 rounded-2xl border-2 border-dashed border-clay/30 bg-clay/[0.03] p-5 text-center" data-testid="m-ingredients-empty">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-clay/10 text-clay">
                  <Archive className="h-5 w-5" />
                </div>
                <p className="mt-3 text-sm text-ink-700">No ingredients added yet for this date.</p>
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={autoFillFromMealPlan}
                    disabled={inputsBlocked}
                    data-testid="m-auto-fill-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-moss/20 px-5 py-3 text-sm font-bold text-moss-dark transition-all active:bg-moss/30 disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" /> Auto-fill from Meal Plan
                  </button>
                  <button
                    type="button"
                    onClick={addIngredient}
                    disabled={inputsBlocked}
                    data-testid="m-add-ingredient-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-clay/40 bg-white px-5 py-3 text-sm font-bold text-clay transition-all active:bg-clay-50 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" /> Add Individual Item
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-2.5">
                {(draft.ingredients || []).map((ing, idx) => (
                  <div key={ing.id || idx} className={`rounded-2xl border p-3 ${ing.isAuto ? "border-moss/30 bg-moss/5" : "border-border bg-sand-50"}`} data-testid={`m-ing-row-${idx}`}>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={ing.name}
                        disabled={blocked}
                        onChange={(e) => updateIngredient(idx, "name", e.target.value)}
                        placeholder="Ingredient name"
                        className="flex-1 bg-transparent text-sm font-semibold text-ink-900 outline-none placeholder:text-ink-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeIngredient(idx)}
                        disabled={blocked}
                        className="grid h-8 w-8 place-items-center rounded-full text-destructive transition-all active:bg-red-50 disabled:opacity-40"
                        aria-label="Remove ingredient"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <label className="block">
                        <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Qty</span>
                        <input type="number" value={ing.quantity} disabled={blocked} onChange={(e) => updateIngredient(idx, "quantity", e.target.value)}
                          className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-right font-mono text-sm text-ink-900 outline-none focus:border-clay" />
                      </label>
                      <label className="block">
                        <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Unit</span>
                        <input type="text" value={ing.unit} disabled={blocked} onChange={(e) => updateIngredient(idx, "unit", e.target.value)}
                          className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-center text-sm text-ink-900 outline-none focus:border-clay" />
                      </label>
                      <label className="block">
                        <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Rate</span>
                        <input type="number" value={ing.rate} disabled={blocked} onChange={(e) => updateIngredient(idx, "rate", e.target.value)}
                          className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1.5 text-right font-mono text-sm text-ink-900 outline-none focus:border-clay" />
                      </label>
                    </div>
                    <p className="mt-2 text-right text-sm font-bold text-clay">{money(ingredientAmount(ing))}</p>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addIngredient}
                  disabled={inputsBlocked}
                  data-testid="m-add-ingredient-btn-inline"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-clay/40 bg-white px-5 py-2.5 text-sm font-bold text-clay transition-all active:bg-clay-50 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add Individual Item
                </button>
                {breakdown && breakdown.mealType !== "manual" && (
                  <button
                    type="button"
                    onClick={autoFillFromMealPlan}
                    disabled={inputsBlocked}
                    data-testid="m-auto-fill-btn-2"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-moss/15 px-5 py-2.5 text-sm font-bold text-moss-dark transition-all active:bg-moss/25 disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" /> Re-fill from Meal Plan
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Target per student */}
          <section className="mx-4 mt-4 rounded-2xl bg-sand-100 p-4" data-testid="m-target-pill">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-clay/15 text-clay">
                <Info className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[0.6rem] font-semibold uppercase tracking-[0.22em] text-ink-700">Target per student</p>
                <p className="mt-0.5 text-sm font-semibold text-ink-900">
                  Pri: <b className="text-clay">{money(TARGET_COST.primary)}</b>
                  <span className="mx-1.5 text-ink-500">•</span>
                  U-Pri: <b className="text-clay">{money(TARGET_COST.upperPrimary)}</b>
                </p>
              </div>
            </div>
          </section>
          </>)}
        </>
      )}

      {/* Mobile month ledger — mirrors the desktop `Entries this month`
          sidebar so phone users get the same scannable, tap-to-edit list
          of per-date entries with classwise beneficiary chips. Shown in
          BOTH single and bulk modes. */}
      <section className="mx-4 mt-4" data-testid="m-entries-list">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-clay">
            {monthLabel} {year} · Ledger
          </p>
          <button
            type="button"
            onClick={refresh}
            className="rounded-full border border-border bg-white px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-700 active:bg-sand-100"
            data-testid="m-entries-refresh-btn"
            aria-label="Refresh entries"
          >
            Refresh
          </button>
        </div>
        {entries.length === 0 ? (
          <div className="rounded-2xl border border-border bg-white p-4 text-center text-xs text-ink-700" data-testid="m-entries-empty">
            No entries for {monthLabel} {year} yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {[...entries].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e) => {
              const isCurrent = e.date === date;
              const isDR = e.entryType === "dryration";
              const isNoMdmE = isNoMdmDay(e);
              const isHolidayE = isHolidayDay(e);
              const att = e.attendance || {};
              const attRows = [
                { key: "bv", label: "BV", count: toNumber(att.balVatika) },
                { key: "p", label: "P", count: toNumber(att.primary) },
                { key: "up", label: "UP", count: toNumber(att.upperPrimary) },
                { key: "hs", label: "HS", count: toNumber(att.highSchool) },
              ];
              const attTotal = attRows.reduce((s, r) => s + r.count, 0);
              const itemsCount = (e.ingredients || []).length;
              const total = (e.ingredients || []).reduce((s, i) => s + ingredientAmount(i), 0);
              // iter154 — mobile ledger badge + reason
              const badgeLabel = isHolidayE ? "HOLIDAY" : isNoMdmE ? "NO MDM" : isDR ? "DR" : "MDM";
              const badgeCls = isHolidayE
                ? "bg-slate-500 text-white"
                : isNoMdmE
                  ? "bg-slate-400 text-white"
                  : isDR ? "bg-ochre text-white" : "bg-moss text-white";
              const reasonTxt = isNoMdmE
                ? (e.noMdmReason === "other" ? (e.noMdmReasonOther || "Other") : reasonLabel(e.noMdmReason || "", "en"))
                : "";
              return (
                <li
                  key={e.id}
                  data-testid={`m-daily-entry-${e.date}`}
                  data-entry-type={isDR ? "DR" : isNoMdmE ? "NO_MDM" : isHolidayE ? "HOLIDAY" : "MDM"}
                  data-day-kind={e.dayKind || "working"}
                  className={`rounded-2xl border p-3 transition-all active:scale-[0.99] ${
                    isCurrent
                      ? "border-clay bg-clay-50"
                      : isHolidayE || isNoMdmE
                        ? "border-slate-300 bg-slate-50"
                        : isDR
                          ? "border-ochre/40 bg-ochre/5"
                          : "border-moss/30 bg-moss/5"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => { setMode("single"); setDate(e.date); if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="block w-full text-left"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-ink-900">
                        {e.date}
                        <span
                          className={`ml-2 rounded-full px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] ${badgeCls}`}
                          data-testid={`m-daily-entry-badge-${e.date}`}
                        >
                          {badgeLabel}
                        </span>
                      </p>
                      {!isNoMdmE && !isHolidayE && (
                        <span className="text-[0.65rem] font-medium text-ink-700">
                          {itemsCount} item{itemsCount === 1 ? "" : "s"} · {money(total)}
                        </span>
                      )}
                    </div>
                    {isNoMdmE && reasonTxt && (
                      <p
                        className="mt-1 text-[0.65rem] font-medium italic text-slate-600"
                        data-testid={`m-daily-entry-reason-${e.date}`}
                      >
                        {reasonTxt}
                      </p>
                    )}
                    {!isNoMdmE && !isHolidayE && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`m-daily-entry-att-${e.date}`}>
                      {attRows.map((r) => (
                        <span
                          key={r.key}
                          data-testid={`m-daily-entry-att-${e.date}-${r.key}`}
                          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[0.65rem] font-medium ${
                            r.count > 0
                              ? "bg-white text-ink-900 ring-1 ring-inset ring-moss/40"
                              : "bg-sand-100 text-ink-500"
                          }`}
                          title={`${r.label} beneficiaries`}
                        >
                          <span className="font-semibold uppercase tracking-[0.08em] text-ink-700">{r.label}</span>
                          <span className="font-mono">{r.count}</span>
                        </span>
                      ))}
                      <span
                        className="inline-flex items-center gap-1 rounded-md bg-clay/10 px-1.5 py-0.5 text-[0.65rem] font-semibold text-clay"
                        data-testid={`m-daily-entry-att-total-${e.date}`}
                        title="Total beneficiaries"
                      >
                        <Users className="h-3 w-3" />
                        <span className="font-mono">{attTotal}</span>
                      </span>
                    </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Spacer so content above the sticky Save button doesn't get clipped.
          BottomNav is ~64px, Save button sits at bottom-[72px] and is
          ~64px tall → reserve ~176px so the last row is fully scrollable. */}
      <div className="h-44" aria-hidden />

      {/* Sticky Save bar — rendered via shared StickyActionBar which
          portals to <body> so it escapes the .page-transition containing
          block (iter69/iter146 RCA). */}
      <StickyActionBar
        testId="m-save-btn"
        onClick={handleSave}
        disabled={saving || blocked}
        secondary={lastUpdated ? `Last updated: ${lastUpdated}` : null}
        secondaryTestId="m-last-updated"
      >
        <Save className="h-4 w-4" /> {saving ? "Saving…" : draft.id ? "Update Entry" : "Save Daily Entry"}
      </StickyActionBar>
    </div>
  );
}
